/**
 * ExamOrderPanel — Panel para ordenar exámenes progresivamente
 * Funciona en modo aula (profesor) y modo individual (alumno)
 */

import { ClinicalScenario, OrderableExam, SCENARIOS } from './case-scenarios';
import { Patient } from '../medical/Patient';
import { PatientGraph, ClinicalNodeData } from '../types/medical';
import { PatientLoader } from '../data/PatientLoader';
import state from '../core/state';

type SessionMode = 'classroom' | 'individual';

interface OrderedExam {
  examId: string;
  orderedAt: number;
  revealedAt?: number;
}

export class ExamOrderPanel {
  private container: HTMLElement | null = null;
  private narrativeEl: HTMLElement | null = null;
  private scenario: ClinicalScenario | null = null;
  private patient: Patient | null = null;
  private patientLoader = new PatientLoader();
  private mode: SessionMode = 'classroom';
  private orderedExams: OrderedExam[] = [];
  private revealedExamIds: Set<string> = new Set();
  private onNodeAdded?: (nodes: ClinicalNodeData[]) => void;

  constructor() {
    this.injectStyles();
  }

  async startScenario(
    scenarioId: string,
    loadPatientFn: (data: PatientGraph) => Promise<void>,
    mode: SessionMode = 'classroom',
    onNodeAdded?: (nodes: ClinicalNodeData[]) => void
  ): Promise<void> {
    this.scenario = SCENARIOS[scenarioId];
    if (!this.scenario) return;

    this.mode = mode;
    this.onNodeAdded = onNodeAdded;
    this.orderedExams = [];
    this.revealedExamIds.clear();

    // Construir PatientGraph con solo las ondas autoReveal
    const initialNodes: ClinicalNodeData[] = [];
    this.scenario.waves.forEach(wave => {
      if (wave.autoReveal) {
        initialNodes.push(...wave.nodes);
      }
    });

    const patientData: PatientGraph = {
      id: `CASE-${scenarioId.toUpperCase()}`,
      demographics: this.scenario.demographics,
      triagePriority: 'minor', // Empieza sin triage definido
      chiefComplaint: this.scenario.chiefComplaint,
      nodes: initialNodes,
      lastUpdated: new Date().toISOString(),
    };

    // Cargar paciente
    await loadPatientFn(patientData);

    // Guardar referencia (se setea después de loadPatient)
    setTimeout(() => {
      this.render();
      this.showNarrative(this.scenario!.presentation);
    }, 100);
  }

  setPatient(patient: Patient): void {
    this.patient = patient;
  }

  stop(): void {
    this.container?.remove();
    this.narrativeEl?.remove();
    this.container = null;
    this.narrativeEl = null;
    this.scenario = null;
  }

  // ═══════════════════════════════════════════════════════════════════════
  // ORDENAR EXÁMENES
  // ═══════════════════════════════════════════════════════════════════════

  private orderExam(exam: OrderableExam): void {
    if (this.revealedExamIds.has(exam.id)) return;

    this.revealedExamIds.add(exam.id);
    this.orderedExams.push({ examId: exam.id, orderedAt: Date.now() });

    // Anuncia la orden en el bus antes de revelar, para que listeners
    // (ej. UI de "procesando…") puedan reaccionar en conjunto.
    state.emitExamOrdered(exam.id, exam.label, exam.nodes.map(n => n.id));

    // Mostrar "procesando" brevemente
    this.showNarrative(`Procesando: ${exam.label}...`);

    // Simular tiempo de resultado
    const delay = exam.turnaround === 'Inmediato' ? 800 : 1500;

    setTimeout(() => {
      if (!this.patient) return;

      const revealedIds: string[] = [];

      // Agregar nodos al paciente
      exam.nodes.forEach(nodeData => {
        const normalized = this.patientLoader['normalizeNode'](nodeData);
        this.patient!.addNode(normalized);
        const node = this.patient!.getNode(normalized.id);
        if (node) {
          node.createLabel();
          node.label!.visible = state.ui.showLabels;
          node.revealBurst();
        }
        revealedIds.push(normalized.id);
      });

      this.onNodeAdded?.(exam.nodes);

      // Mostrar teaching point
      this.showTeachingPoint(exam);

      // Actualizar UI
      this.updateUI();

      // Propaga cada hallazgo al bus. El ReasoningEngine recalcula patrones
      // y triage; el DifferentialPanel reajusta probabilidades.
      revealedIds.forEach(id => state.emitFindingRevealed(id, 'exam'));

      console.log(`[ExamOrder] Revealed: ${exam.label} (${exam.nodes.length} nodes)`);
    }, delay);
  }

  // ═══════════════════════════════════════════════════════════════════════
  // NARRATIVA Y TEACHING
  // ═══════════════════════════════════════════════════════════════════════

  private showNarrative(text: string): void {
    if (!this.narrativeEl) {
      this.narrativeEl = document.createElement('div');
      this.narrativeEl.className = 'eop-narrative';
      document.body.appendChild(this.narrativeEl);
    }

    this.narrativeEl.innerHTML = `
      <div class="eop-narrative-card">
        <p>${text}</p>
      </div>
    `;
    this.narrativeEl.style.display = 'flex';

    // Auto-ocultar después de 8s
    setTimeout(() => {
      if (this.narrativeEl) this.narrativeEl.style.display = 'none';
    }, 8000);
  }

  private showTeachingPoint(exam: OrderableExam): void {
    this.showNarrative(`
      <strong>${exam.label}</strong>
      ${exam.nodes.map(n => {
        if (n.valueQuantity) {
          const flag = n.referenceRange?.high && n.valueQuantity.value > n.referenceRange.high ? ' ⬆' :
                       n.referenceRange?.low && n.valueQuantity.value < n.referenceRange.low ? ' ⬇' : '';
          return `<br><span style="font-family: 'JetBrains Mono', monospace; color: ${flag ? '#FF453A' : '#30D158'};">${n.concept.display}: ${n.valueQuantity.value} ${n.valueQuantity.unit}${flag}</span>`;
        }
        return `<br><span style="color: #a1a1a6;">${n.concept.display}: ${n.valueString || ''}</span>`;
      }).join('')}
      <br><br><span style="color: #FF9F0A; font-size: 12px;">💡 ${exam.teachingPoint}</span>
    `);
  }

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════

  private render(): void {
    this.container?.remove();
    this.container = document.createElement('div');
    this.container.id = 'exam-order-panel';
    document.body.appendChild(this.container);
    this.updateUI();
  }

  private updateUI(): void {
    if (!this.container || !this.scenario) return;

    const categories = [
      { key: 'physical_exam', label: 'Examen Físico', icon: '👋' },
      { key: 'laboratory', label: 'Laboratorio', icon: '🧪' },
      { key: 'imaging', label: 'Imágenes', icon: '📷' },
      { key: 'treatment', label: 'Tratamiento', icon: '💊' },
    ];

    this.container.innerHTML = `
      <div class="eop-header">
        <h3>Ordenar Exámenes</h3>
        <span class="eop-count">${this.revealedExamIds.size}/${this.scenario.orderableExams.length}</span>
      </div>

      ${categories.map(cat => {
        const exams = this.scenario!.orderableExams.filter(e => e.category === cat.key);
        if (exams.length === 0) return '';
        return `
          <div class="eop-category">
            <div class="eop-cat-label">${cat.icon} ${cat.label}</div>
            ${exams.map(exam => {
              const revealed = this.revealedExamIds.has(exam.id);
              return `
                <button class="eop-exam ${revealed ? 'revealed' : ''} ${exam.criticalFind ? 'critical' : ''}"
                        data-exam-id="${exam.id}" ${revealed ? 'disabled' : ''}>
                  <div class="eop-exam-info">
                    <span class="eop-exam-name">${exam.label}</span>
                    <span class="eop-exam-time">${exam.turnaround}</span>
                  </div>
                  ${revealed ? '<span class="eop-exam-check">✓</span>' : '<span class="eop-exam-arrow">→</span>'}
                </button>
              `;
            }).join('')}
          </div>
        `;
      }).join('')}

      ${this.scenario.discussionPoints.length > 0 && this.revealedExamIds.size >= 3 ? `
        <div class="eop-discussion">
          <div class="eop-cat-label">💬 Puntos de discusión</div>
          ${this.scenario.discussionPoints.map(p => `
            <div class="eop-disc-point">${p}</div>
          `).join('')}
        </div>
      ` : ''}

      <button class="eop-close-btn" id="eop-close">Cerrar panel</button>
    `;

    // Event listeners
    this.container.querySelectorAll('.eop-exam:not(.revealed)').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = (e.currentTarget as HTMLElement).dataset.examId!;
        const exam = this.scenario!.orderableExams.find(ex => ex.id === id);
        if (exam) this.orderExam(exam);
      });
    });

    this.container.querySelector('#eop-close')?.addEventListener('click', () => this.stop());
  }

  // ═══════════════════════════════════════════════════════════════════════
  // ESTILOS
  // ═══════════════════════════════════════════════════════════════════════

  private injectStyles(): void {
    if (document.getElementById('eop-styles')) return;
    const s = document.createElement('style');
    s.id = 'eop-styles';
    s.textContent = `
      #exam-order-panel {
        position: fixed; right: 80px; top: 20px; bottom: 20px;
        width: 280px;
        background: rgba(10,10,15,0.58);
        backdrop-filter: blur(28px) saturate(140%); -webkit-backdrop-filter: blur(28px) saturate(140%);
        border: 0.5px solid rgba(255,255,255,0.08);
        border-radius: 16px;
        padding: 18px;
        z-index: 200;
        overflow-y: auto;
        font-family: -apple-system, BlinkMacSystemFont, 'Inter', sans-serif;
        color: #f5f5f7;
        display: flex; flex-direction: column; gap: 14px;
        animation: eopIn 0.4s cubic-bezier(0.25,1,0.5,1);
      }
      #exam-order-panel::-webkit-scrollbar { width: 4px; }
      #exam-order-panel::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 2px; }

      .eop-header {
        display: flex; align-items: center; justify-content: space-between;
      }
      .eop-header h3 { font-size: 15px; font-weight: 700; letter-spacing: -0.3px; margin: 0; }
      .eop-count {
        font-family: 'JetBrains Mono', monospace;
        font-size: 11px; color: #6e6e73;
      }

      .eop-category { display: flex; flex-direction: column; gap: 4px; }
      .eop-cat-label {
        font-size: 10px; font-weight: 600; text-transform: uppercase;
        letter-spacing: 1px; color: #6e6e73; margin-bottom: 4px;
      }

      .eop-exam {
        display: flex; align-items: center; justify-content: space-between;
        padding: 10px 12px; border-radius: 10px;
        background: rgba(255,255,255,0.04);
        border: 0.5px solid rgba(255,255,255,0.06);
        cursor: pointer; transition: all 0.25s;
        color: #f5f5f7; font-family: inherit;
        text-align: left; width: 100%;
      }
      .eop-exam:hover:not(.revealed) {
        background: rgba(41,151,255,0.08);
        border-color: rgba(41,151,255,0.2);
        transform: translateX(3px);
      }
      .eop-exam.critical:not(.revealed) {
        border-color: rgba(255,69,58,0.15);
      }
      .eop-exam.critical:hover:not(.revealed) {
        background: rgba(255,69,58,0.08);
        border-color: rgba(255,69,58,0.3);
      }
      .eop-exam.revealed {
        opacity: 0.5; cursor: default;
        background: rgba(48,209,88,0.04);
        border-color: rgba(48,209,88,0.1);
      }
      .eop-exam-info { display: flex; flex-direction: column; gap: 2px; }
      .eop-exam-name { font-size: 12px; font-weight: 600; }
      .eop-exam-time {
        font-size: 10px; color: #6e6e73;
        font-family: 'JetBrains Mono', monospace;
      }
      .eop-exam-arrow { color: #6e6e73; font-size: 14px; }
      .eop-exam-check { color: #30D158; font-size: 14px; }

      .eop-discussion { border-top: 0.5px solid rgba(255,255,255,0.06); padding-top: 12px; }
      .eop-disc-point {
        font-size: 11px; color: #a1a1a6; line-height: 1.5;
        padding: 8px 10px; border-radius: 8px;
        background: rgba(255,255,255,0.02);
        margin-bottom: 4px;
      }

      .eop-close-btn {
        padding: 10px; border-radius: 10px;
        background: rgba(255,255,255,0.04); border: 0.5px solid rgba(255,255,255,0.08);
        color: #6e6e73; font-size: 12px; font-weight: 500;
        cursor: pointer; font-family: inherit;
        transition: all 0.2s;
      }
      .eop-close-btn:hover { background: rgba(255,255,255,0.08); color: #a1a1a6; }

      .eop-narrative {
        position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%);
        z-index: 350; display: none;
        pointer-events: none;
      }
      .eop-narrative-card {
        pointer-events: auto;
        max-width: 560px; padding: 18px 24px;
        background: rgba(0,0,0,0.92);
        backdrop-filter: blur(16px); -webkit-backdrop-filter: blur(16px);
        border: 0.5px solid rgba(255,255,255,0.1);
        border-radius: 14px;
        font-size: 14px; color: #d1d1d6; line-height: 1.6;
        animation: eopNarr 0.4s cubic-bezier(0.25,1,0.5,1);
      }
      .eop-narrative-card strong { color: #f5f5f7; }

      @keyframes eopIn {
        from { opacity: 0; transform: translateX(12px); }
        to { opacity: 1; transform: translateX(0); }
      }
      @keyframes eopNarr {
        from { opacity: 0; transform: translateX(-50%) translateY(8px); }
        to { opacity: 1; transform: translateX(-50%) translateY(0); }
      }
    `;
    document.head.appendChild(s);
  }
}

export const examOrderPanel = new ExamOrderPanel();
export default ExamOrderPanel;
